import { Link, useRouterState } from "@tanstack/react-router";

const BASE = "https://www.akhusbilfritid.se";

// Segment som aldrig ska visas som egen nivå i trappan
const HIDDEN = new Set(["_authenticated"]);

// Rutter där brödsmulor inte hör hemma
const EXCLUDED_PREFIXES = [
  "/login",
  "/admin",
  "/konto",
  "/sparade",
  "/unsubscribe",
  "/email",
  "/api",
  "/lovable",
  "/.mcp",
  "/.well-known",
];

// Rutter som redan renderar egen BreadcrumbList i sin head()
const SCHEMA_HANDLED: string[] = [
  "/blogg",
  "/guider",
  "/fordon",
  "/rapido",
  "/husbil",
  "/husbilar",
  "/husvagnar",
  "/husbil-kalix",
  "/husbil-norrbotten",
  "/husbil-matchning",
];

// Sektioner där bara undersidorna har eget schema (indexsidan behöver vårt)
const SCHEMA_HANDLED_CHILDREN: string[] = ["/campingtips", "/ordlista", "/jamfor"];

// Kända etiketter för segment som inte blir snygga av ren slug-formatering
const LABELS: Record<string, string> = {
  fordon: "Fordon",
  blogg: "Blogg",
  guider: "Guider",
  campingtips: "Campingtips",
  rapido: "Rapido",
  reservdelar: "Reservdelar",
  fritid: "Fritid",
  fakta: "Fakta",
  ordlista: "Ordlista",
  jamfor: "Jämför",
  verktyg: "Verktyg",
  modeller: "Modeller",
  begagnade: "Begagnade",
  prislista: "Prislista",
  aterforsaljare: "Återförsäljare",
  husbil: "Husbil",
  husbilar: "Husbilar",
  husvagnar: "Husvagnar",
  kontakt: "Kontakt",
  "om-oss": "Om oss",
  cookies: "Cookies",
  integritetspolicy: "Integritetspolicy",
  anvandarvillkor: "Användarvillkor",
  "husbil-matchning": "Husbilsmatchning",
  "husbilar-till-salu": "Husbilar till salu",
  "husvagnar-till-salu": "Husvagnar till salu",
};

/**
 * Vissa mellansegment i URL:en har ingen egen sida (/husbil, /verktyg,
 * /rapido/modeller). Länka dem till närmaste riktiga hubb – annars får
 * crawlers 404-länkar i brödsmulorna.
 */
const PARENT_REWRITE: Record<string, string> = {
  "/husbil": "/husbilar-till-salu",
  "/husvagn": "/husvagnar-till-salu",
  "/husbilar": "/husbilar-till-salu",
  "/husvagnar": "/husvagnar-till-salu",
  "/husbil-arsmodell": "/fakta",
  "/husbil-till-salu-under": "/husbilar-till-salu",
  "/rapido/modeller": "/rapido",
  "/rapido/guider": "/rapido",
  "/blogg/amne": "/blogg",
  "/forum/t": "/forum",
  "/forum/k": "/forum",
  "/forum/tagg": "/forum",
  "/forum/medlem": "/forum",
  "/verktyg": "/vardera-husbil",
};

/** Mellansegment vars hubb ligger en nivå upp (t.ex. /modeller/kabe → /modeller). */
const PARENT_REWRITE_PREFIX: Array<[RegExp, string]> = [
  [/^\/modeller\/[^/]+$/, "/modeller"],
  [/^\/reservdelar\/[^/]+\//, "/reservdelar"],
];


function prettify(segment: string): string {
  const known = LABELS[segment];
  if (known) return known;
  const decoded = decodeURIComponent(segment).replace(/-/g, " ");
  return decoded.charAt(0).toUpperCase() + decoded.slice(1);
}

export function SiteBreadcrumbs() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const clean = pathname.replace(/\/+$/, "");
  if (!clean || clean === "/") return null;
  if (EXCLUDED_PREFIXES.some((p) => clean === p || clean.startsWith(`${p}/`))) return null;

  const segments = clean.split("/").filter((s) => s && !HIDDEN.has(s));
  if (segments.length === 0) return null;

  const crumbs = segments.map((segment, i) => ({
    label: prettify(segment),
    href: (() => {
      const raw = `/${segments.slice(0, i + 1).join("/")}`;
      if (PARENT_REWRITE[raw]) return PARENT_REWRITE[raw];
      if (i < segments.length - 1) {
        const hit = PARENT_REWRITE_PREFIX.find(([re]) => re.test(raw));
        if (hit) return hit[1];
      }
      return raw;
    })(),
  }));


  // Sidor som redan skickar egen BreadcrumbList i sin head() — två likadana
  // scheman på samma URL gör att Google struntar i båda.
  const schemaHandled =
    SCHEMA_HANDLED.some((p) => clean === p || clean.startsWith(`${p}/`)) ||
    SCHEMA_HANDLED_CHILDREN.some((p) => clean.startsWith(`${p}/`));

  const jsonLd = schemaHandled
    ? null
    : {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Hem", item: `${BASE}/` },
          ...crumbs.map((c, i) => ({
            "@type": "ListItem",
            position: i + 2,
            name: c.label,
            item: `${BASE}${c.href}`,
          })),
        ],
      };

  return (
    <nav
      aria-label="Brödsmulor"
      className="mx-auto w-full max-w-7xl px-4 pt-4 md:px-8 md:pt-6"
    >
      <ol className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px] text-muted-foreground">
        <li>
          <Link to="/" className="transition hover:text-accent">
            Hem
          </Link>
        </li>
        {crumbs.map((c, i) => {
          const isLast = i === crumbs.length - 1;
          return (
            <li key={c.href} className="flex items-center gap-2">
              <span aria-hidden="true" className="text-muted-foreground/50">
                ›
              </span>
              {isLast ? (
                <span aria-current="page" className="text-foreground/80">
                  {c.label}
                </span>
              ) : (
                <Link to={c.href} className="transition hover:text-accent">
                  {c.label}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
      {jsonLd ? (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />
      ) : null}
    </nav>
  );
}
